import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import {
  Search,
  Plus,
  Building2,
  MapPin,
  User,
  ChevronRight,
  AlertCircle,
  Check,
  X
} from 'lucide-react';

interface Club {
  id: string;
  name: string;
  slug: string | null;
  city: string;
  province: string;
  isActive: boolean;
  owner: {
    id: string;
    name: string;
    email: string;
  };
  venues: {
    id: string;
    name: string;
    venueType: string;
    status: string;
  }[];
  _count: {
    venues: number;
  };
}

const AdminClubsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchClubs();
  }, [searchTerm, statusFilter]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(
        getApiUrl(`/admin/clubs?${params.toString()}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar clubs');
      }

      const data = await response.json();
      setClubs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: theme.fonts.body,
  };

  const filtersContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  };

  const searchInputContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.input,
    border: `1px solid ${theme.colors.border}`,
    padding: '0 12px',
    flex: '1',
    minWidth: '250px',
  };

  const searchInputStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
  };

  const selectStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    cursor: 'pointer',
    minWidth: '150px',
  };

  const tableContainerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '16px',
    backgroundColor: 'rgba(0, 230, 118, 0.05)',
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    fontFamily: theme.fonts.heading,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: '16px',
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const rowStyle: React.CSSProperties = {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  };

  const badgeStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    backgroundColor: isActive
      ? 'rgba(0, 230, 118, 0.1)'
      : 'rgba(255, 23, 68, 0.1)',
    color: isActive ? theme.colors.primary : theme.colors.danger,
  });

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '48px 24px',
    color: theme.colors.textSecondary,
  };

  if (loading && clubs.length === 0) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ color: theme.colors.textSecondary }}>Cargando...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Clubs</h1>
        <button
          style={buttonStyle}
          onClick={() => navigate('/admin/clubs/create')}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#00D96F';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.primary;
          }}
        >
          <Plus size={20} />
          Crear Club
        </button>
      </div>

      <div style={filtersContainerStyle}>
        <div style={searchInputContainerStyle}>
          <Search size={20} color={theme.colors.textSecondary} />
          <input
            type="text"
            placeholder="Buscar por nombre o ciudad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="all">Todos</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
      </div>

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px',
          backgroundColor: 'rgba(255, 23, 68, 0.1)',
          borderRadius: theme.borderRadius.card,
          color: theme.colors.danger,
          marginBottom: '24px',
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Club</th>
              <th style={thStyle}>Propietario</th>
              <th style={thStyle}>Ubicacion</th>
              <th style={thStyle}>Canchas</th>
              <th style={thStyle}>Estado</th>
              <th style={{ ...thStyle, width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {clubs.length === 0 ? (
              <tr>
                <td colSpan={6} style={emptyStateStyle}>
                  <Building2 size={48} color={theme.colors.textSecondary} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>No se encontraron clubs</p>
                </td>
              </tr>
            ) : (
              clubs.map((club) => (
                <tr
                  key={club.id}
                  style={rowStyle}
                  onClick={() => navigate(`/admin/clubs/${club.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 230, 118, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(0, 230, 118, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Building2 size={20} color={theme.colors.primary} />
                      </div>
                      <div>
                        <div style={{ fontWeight: theme.fonts.weightBold }}>{club.name}</div>
                        {club.slug && (
                          <div style={{
                            fontSize: theme.fonts.sizeSmall,
                            color: theme.colors.textSecondary
                          }}>
                            /club/{club.slug}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User size={16} color={theme.colors.textSecondary} />
                      <div>
                        <div>{club.owner.name}</div>
                        <div style={{
                          fontSize: theme.fonts.sizeSmall,
                          color: theme.colors.textSecondary
                        }}>
                          {club.owner.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MapPin size={16} color={theme.colors.textSecondary} />
                      <span>{club.city}, {club.province}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      color: '#2196F3',
                      fontSize: theme.fonts.sizeSmall,
                      fontWeight: theme.fonts.weightBold,
                    }}>
                      {club._count.venues} canchas
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={badgeStyle(club.isActive)}>
                      {club.isActive ? <Check size={14} /> : <X size={14} />}
                      {club.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <ChevronRight size={20} color={theme.colors.textSecondary} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminClubsPage;
